import { useResourceContext } from "ra-core";

import { BooleanInput } from "@/components/admin/boolean-input";

import { COUNTS_AS_TOUCH_DEFAULT, hasCountsAsTouch } from "./countsAsTouch";

/**
 * The opt-out on a note: "this is a note, but it is not contact."
 *
 * WHY THE DEFAULT IS ON
 * ---------------------
 * Most notes record contact, and the cadence clock is only useful if it is
 * fed without anyone remembering to feed it. `contact_notes.counts_as_touch`
 * therefore defaults to true in the database, and a note written anywhere --
 * including screens that never render this input -- still stamps a touch.
 *
 * WHY THE OPT-OUT HAS TO EXIST ANYWAY
 * ------------------------------------
 * "Saw they raised a round" and "left them a voicemail" are both notes, and
 * only one of them is contact. Without a way to say so, research resets the
 * clock: you write down what you learned about someone, and the system now
 * believes you spoke to them and stops chasing you for six weeks. That is the
 * failure mode this project is named after, produced by the act of being
 * diligent.
 *
 * WHY IT IS REVERSIBLE
 * --------------------
 * The trigger in 050_engagement_triggers.sql keys on the unique
 * `engagements.source_note_id`, so ticking upserts the touch and unticking
 * DELETES it -- and `last_contacted_at` is recomputed rather than nudged, so
 * it falls back again. Getting this wrong is therefore a correctable mistake
 * on both screens, which is the only reason it is safe to default it on.
 *
 * Renders nothing outside `contact_notes`; see ./countsAsTouch.ts for why that
 * gate is load-bearing rather than cosmetic.
 */
export const CountsAsTouchInput = ({ className }: { className?: string }) => {
  const resource = useResourceContext();

  if (!hasCountsAsTouch(resource)) return null;

  return (
    <BooleanInput
      source="counts_as_touch"
      label="Counts as contact"
      defaultValue={COUNTS_AS_TOUCH_DEFAULT}
      helperText="On: resets this person's follow-up clock. Turn off for research or an internal note — something you learned about them rather than a conversation with them."
      className={className}
    />
  );
};
